import React, { useState, useRef, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';

import { singleFileUpload } from '../../api/common';
import { FILE_TYPES, FILE_TYPE_EXTENSIONS } from '../../utils/constants/common';
import DefaultImage from '../../assets/images/default-picture.png';
import useToast from '../../hooks/useToast';

const FileUploader = ({
  folderName,
  isReplace,
  replaceFileURL,
  displayImg = false,
  allowedTypes = [FILE_TYPES.IMAGE, FILE_TYPES.PDF, FILE_TYPES.HTML, FILE_TYPES.CSV],
  maxFileSize = 2 * 1024 * 1024, // 2MB
  onFileUpload,
  file: initialFile = null,
}) => {
  const { showToast } = useToast();
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputFile = useRef(null);
  const isImg = allowedTypes.includes(FILE_TYPES.IMAGE);
  const isImgOrPdf = isImg && allowedTypes.includes(FILE_TYPES.PDF);

  const acceptedFileTypes = allowedTypes
    .flatMap((type) => FILE_TYPE_EXTENSIONS[type]?.map((ext) => `.${ext}`) || [])
    .join(',');

  const validateFile = (file) => {
    const fileExt = file.name.split('.').pop().toLowerCase();
    const type = Object.keys(FILE_TYPE_EXTENSIONS).find((key) =>
      FILE_TYPE_EXTENSIONS[key].includes(fileExt)
    );
    if (!type || !allowedTypes.includes(type)) {
      throw new Error('Invalid file type');
    }
    if (file.size > maxFileSize) {
      throw new Error('File size exceeds the limit');
    }
    return type;
  };
  const uploadFile = async (file) => {
    try {
      setIsLoading(true);
      const bodyData = new FormData();
      bodyData.append('folder', folderName);
      bodyData.append('file', file);
      if (isReplace && replaceFileURL) {
        bodyData.append('isReplace', isReplace);
        bodyData.append('replaceFileURL', replaceFileURL);
      }
      const res = await singleFileUpload(bodyData);
      const fileUrl = res.data.file;
      onFileUpload(fileUrl);
      setSelectedFile(fileUrl);
    } catch (error) {
      showToast('File could not be uploaded', 'error');
    } finally {
      reset();
      setIsLoading(false);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const type = validateFile(file);
      setFileType(type);
      await uploadFile(file);
    } catch (error) {
      showToast('Please select valid file type', 'error');
      reset();
    }
  };

  const handleFileInput = () => {
    inputFile.current.click();
  };

  const reset = () => {
    setSelectedFile(null);
    setIsLoading(false);
    inputFile.current.value = null;
  };

  const setFile = (file) => {
    const fileExt = file.split('.').pop();
    const type = Object.keys(FILE_TYPE_EXTENSIONS).find((key) =>
      FILE_TYPE_EXTENSIONS[key].includes(fileExt)
    );
    setFileType(type);
    setSelectedFile(file);
  };

  useEffect(() => {
    if (initialFile) {
      setFile(initialFile);
    }
  }, [initialFile]);

  return (
    <Box className="file-uploader">
      {displayImg && !selectedFile && (
        <img src={DefaultImage} alt="default" className="attached-documents" />
      )}
      {selectedFile && displayImg && fileType === FILE_TYPES.IMAGE && (
        <img src={selectedFile} alt="uploaded" className="attached-documents" />
      )}
      {selectedFile && displayImg && fileType === FILE_TYPES.PDF && (
        <a href={selectedFile} target="_blank" rel="noreferrer" className="pdf-document">
          <i className="fas fa-file-pdf"></i>
        </a>
      )}
      {isLoading && <CircularProgress color="secondary" className="primary-loader" />}
      <Box display="flex" alignItems="center" onClick={handleFileInput}>
        <i className="fas fa-file-upload ic-file-upload-icon" />
        <Box pl={1}>
          <Typography color="grey">Choose File</Typography>
          <Typography color="grey">
            {`${isImgOrPdf ? 'JPG, SVG, PNG or PDF. ' : isImg ? 'JPG, SVG, or PNG. ' : ''}Max size of ${maxFileSize / (1024 * 1024)}MB`}
          </Typography>
        </Box>
        <input
          type="file"
          accept={acceptedFileTypes}
          ref={inputFile}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </Box>
    </Box>
  );
};

export default FileUploader;
